import {parseWithZod} from '@conform-to/zod'
import {invariantResponse} from '@epic-web/invariant'
import {type ActionFunctionArgs, data} from '@remix-run/node'
import {redirect, useFetcher, useFetchers} from '@remix-run/react'
import {Laptop, Moon, Sun} from 'lucide-react'
import {z} from 'zod'

import {Tooltip, TooltipProvider, TooltipContent, TooltipTrigger} from '#app/ui/tooltip'
import {useHints, useOptionalHints} from '#app/utils/client-hints.tsx'
import {cn} from '#app/utils/misc'
import {useOptionalRequestInfo, useRequestInfo} from '#app/utils/request-info.ts'
import {setTheme} from '#app/utils/theme.server.ts'

const ThemeFormSchema = z.object({
  theme: z.enum(['light', 'dark', 'system']),
  // this is useful for progressive enhancement
  redirectTo: z.string().optional(),
})
type ThemeMode = z.infer<typeof ThemeFormSchema>['theme']

export async function action({request}: ActionFunctionArgs) {
  const formData = await request.formData()
  const submission = parseWithZod(formData, {
    schema: ThemeFormSchema,
  })

  invariantResponse(submission.status === 'success', 'Invalid theme received')

  const {theme, redirectTo} = submission.value

  const responseInit = {
    headers: {'set-cookie': setTheme(theme)},
  }
  if (redirectTo) {
    return redirect(redirectTo, responseInit)
  } else {
    return data({result: submission.reply()}, responseInit)
  }
}

export function ThemeSwitch() {
  const requestInfo = useRequestInfo()
  const optimisticMode = useOptimisticThemeMode()
  const fetcher = useFetcher<typeof action>()

  const selected = optimisticMode ?? requestInfo.userPreferences.theme ?? 'system'

  const handleSubmit = (theme: ThemeMode) => {
    fetcher.submit(
      {theme, redirectTo: requestInfo.path},
      {method: 'POST', action: '/resources/theme-switch'},
    )
  }

  return (
    <TooltipProvider>
      <div className="flex rounded-full border">
        {[
          {mode: 'light' as const, Icon: Sun, label: 'Light'},
          {mode: 'dark' as const, Icon: Moon, label: 'Dark'},
          {mode: 'system' as const, Icon: Laptop, label: 'System'},
        ].map(({mode, Icon, label}) => (
          <Tooltip key={mode}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={cn(
                  'flex size-6 cursor-pointer items-center justify-center rounded-full',
                  selected === mode && 'border bg-accent text-accent-foreground',
                )}
                onClick={() => handleSubmit(mode as ThemeMode)}
              >
                <Icon className="size-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{label}</TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  )
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
export function useOptimisticThemeMode() {
  const fetchers = useFetchers()
  const themeFetcher = fetchers.find(f => f.formAction === '/resources/theme-switch')

  if (themeFetcher && themeFetcher.formData) {
    const submission = parseWithZod(themeFetcher.formData, {
      schema: ThemeFormSchema,
    })

    if (submission.status === 'success') {
      return submission.value.theme
    }
  }
}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme() {
  const hints = useHints()
  const requestInfo = useRequestInfo()
  const optimisticMode = useOptimisticThemeMode()
  if (optimisticMode) {
    return optimisticMode === 'system' ? hints.theme : optimisticMode
  }
  const theme = requestInfo.userPreferences.theme ?? hints.theme
  return theme
}

export function useOptionalTheme() {
  const optionalHints = useOptionalHints()
  const optionalRequestInfo = useOptionalRequestInfo()
  const optimisticMode = useOptimisticThemeMode()
  if (optimisticMode) {
    return optimisticMode === 'system' ? optionalHints?.theme : optimisticMode
  }
  return optionalRequestInfo?.userPreferences.theme ?? optionalHints?.theme
}
